import {
    HorizontalContainer,
    VerticalContainer,
    WholeNumberInput,
    Wrapper
} from '@american-autonomy/ag-ui';

import { getUnitLabels } from 'pages/MaintenancePage/helpers/getUnitLabels';
import useTranslation from 'pages/MaintenancePage/hooks/useTranslation';

import { REMAINING_LIFE_INPUT_NAME, TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

// SR-23 — installed part condition for a replacement: a new part starts life at
// zero, while a used part carries prior usage, captured as adjustable remaining
// life against the part's configured interval.
const PartConditionField: FunctionComponent<Props> = ({
    className = '',
    interval,
    isRemainingLifeValid,
    onChangePartCondition,
    onChangeRemainingLife,
    partCondition,
    remainingLife,
    testId = TEST_ID
}) => {
    const t = useTranslation();
    const newSelectedClass = partCondition === 'new' ? styles.isSelected : '';
    const usedSelectedClass = partCondition === 'used' ? styles.isSelected : '';

    const unitLabels = getUnitLabels(t);

    const handleChangeRemainingLife = (value: number) =>
        onChangeRemainingLife({ remainingLife: value });

    return (
        <VerticalContainer className={`${styles.partConditionField} ${className}`} testId={testId}>
            <Wrapper className={styles.label}>{t.LOG_SERVICE_PART_CONDITION}</Wrapper>

            <HorizontalContainer className={styles.options}>
                <Wrapper
                    className={`${styles.option} ${newSelectedClass}`}
                    onClick={() => onChangePartCondition({ partCondition: 'new' })}
                    testId={`${testId}-new`}>
                    {t.PART_CONDITION_NEW}
                </Wrapper>

                <Wrapper
                    className={`${styles.option} ${usedSelectedClass}`}
                    onClick={() => onChangePartCondition({ partCondition: 'used' })}
                    testId={`${testId}-used`}>
                    {t.PART_CONDITION_USED}
                </Wrapper>
            </HorizontalContainer>

            {partCondition === 'used' && (
                <VerticalContainer className={styles.remainingLife}>
                    <Wrapper className={styles.remainingLifeLabel}>
                        {t.LOG_SERVICE_REMAINING_LIFE}
                    </Wrapper>

                    <HorizontalContainer className={styles.remainingLifeRow}>
                        <WholeNumberInput
                            className={styles.remainingLifeInput}
                            name={REMAINING_LIFE_INPUT_NAME}
                            onChange={handleChangeRemainingLife}
                            testId={`${testId}-remainingLifeInput`}
                            value={remainingLife}
                        />

                        <Wrapper className={styles.unitSuffix}>{unitLabels[interval.unit]}</Wrapper>
                    </HorizontalContainer>

                    {!isRemainingLifeValid && (
                        <Wrapper className={styles.error}>
                            {t.REMAINING_LIFE_INVALID({ max: interval.value })}
                        </Wrapper>
                    )}
                </VerticalContainer>
            )}
        </VerticalContainer>
    );
};

export default PartConditionField;
